import React, { useRef } from 'react';

import {
    Ad,
    AdContent,
    AdContentDetails,
    AdContentTitle,
    AdImage,
    AdImageFrame,
    AdImageFrameOverlay,
    AdLink,
    AdLogo,
} from '@finn-no/troika-react-ads';
import { ImageFormat } from '@finn-no/troika-react-auto-image-format';
import {
    useExternalClickTracking,
    useInstanceId,
    useTrackInscreen,
} from './hooks';
import Tracking from './tracking';

import {
    Sponsored,
    DeviceType,
    PageType,
    useNativeAd,
    TransparencyButton,
} from '@finn-no/displayads-client';

const bannerPageType = (position: string): PageType => {
    if (position.indexOf('-object_') > -1) {
        return 'object';
    }
    if (position.indexOf('-browse_') > -1) {
        return 'section';
    }
    if (position.indexOf('-frontpage_') > -1) {
        return 'front';
    }
};

type NativeAdProps = {
    ad: import('./types').Recommendation;
    trackAdClick: (adId: string, trackingUrlClick: string[]) => void;
    tracking: Tracking;
    position: string;
    deviceType: DeviceType;
};
const NativeAd: React.FC<NativeAdProps> = (props) => {
    const { tracking, ad, position, deviceType, trackAdClick } = props;
    const inscreenTrackingUrls = Tracking.getInscreenUrl(ad);
    const trackingUrlClick = Tracking.getClickUrl(ad);
    const domRef = useRef<HTMLDivElement>(null);
    const instanceId = useInstanceId((id) => `ad-stream__native-ad-${id}`);
    const pageType = bannerPageType(position);

    const { data, loaded } = useNativeAd(instanceId, pageType, deviceType, {
        bap_main_category: ad.content.main_category,
        bap_product_category: ad.content.prod_category,
        bap_sub_category: ad.content.sub_category,
    });
    useTrackInscreen({
        adId: ad.itemId,
        ref: domRef,
        tracking,
        enabled: loaded && !!data,
        inscreenTrackingUrls,
        pulseContext: ad?.tracking?.pulse,
    });
    useExternalClickTracking({
        adId: ad.itemId,
        ref: domRef,
        enabled: loaded && !!data,
        trackAdClick,
        trackingUrlClick,
        tracking,
        pulseContext: ad?.tracking?.pulse,
    });

    if (loaded) {
        if (data) {
            const { native } = data;
            const { image, icon, title, body, clickUrl } = native;

            return (
                <Ad key={ad.itemId} ref={domRef} id={instanceId}>
                    <AdImage>
                        <AdImageFrame>
                            <ImageFormat
                                alt=""
                                src={image?.url}
                                ratio="1by1"
                                className="u-bg-milk"
                                centered
                                crop="horizontal"
                            />
                            {body && (
                                <AdImageFrameOverlay className="u-d1">
                                    {body}
                                </AdImageFrameOverlay>
                            )}
                            <TransparencyButton
                                adObj={data}
                                placement="right"
                                style={{
                                    top: '-2px',
                                    right: '-1px',
                                }}
                            ></TransparencyButton>
                        </AdImageFrame>
                    </AdImage>
                    <AdContent>
                        <AdContentTitle lineClamp2>
                            <AdLink
                                href={clickUrl}
                                target="_blank"
                                title={title}
                            >
                                {title}
                            </AdLink>
                        </AdContentTitle>
                        <AdContentDetails className="u-mb8">
                            <Sponsored native={native} />
                        </AdContentDetails>
                    </AdContent>

                    <AdLogo>
                        <img src={icon.url} alt="" />
                    </AdLogo>
                </Ad>
            );
        }
        return null;
    }
    return <Ad key={ad.itemId} ref={domRef} id={instanceId}></Ad>;
};

export default NativeAd;
